import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { useAuth } from "../utils/useAuth";
import { reportError } from "../utils/reportError";
import { CONTACT_EMAIL } from "../utils/contact";
import { useJudgeRaces } from "../utils/useJudgeRaces";
import { formatCzDate } from "../utils/dates";
import { useNewAppVersion } from "../utils/useNewAppVersion";
import { AccountMenu } from "./AccountMenu";
import { LoadingState } from "./AsyncState";
import { Modal } from "./ConfirmDialog";
import { isChunkLoadError, resetFailedLazyRoutes, usePreloadWhenIdle } from "./lazyRoute";
import { LIVE_SCREENS } from "../liveScreens";
import "./ui.css";

/**
 * AppShell – jednotná kostra obrazovky: fixní hlavička (user/admin/judge),
 * scrollovatelný <main> (jediný scroll kontejner) a spodní navigace.
 * Nahrazuje MainLayout, AdminLayout, BottomNav a ScrollToTop.
 *
 * Použití jako layout route (App.tsx): <Route element={<AppShell variant="user" />}>
 * – obsah dodá <Outlet/>. Stránka pak nastaví titulek přes <Screen title=...>
 * nebo hook useAppHeader. Samostatně: <AppShell variant="admin" title="…">obsah</AppShell>.
 *
 * Vpravo v hlavičce za akcemi stránky je účet přihlášeného (AccountMenu –
 * avatar s iniciálami → dialog s odhlášením), jen na obrazovkách bez Zpět.
 *
 * Výšky hlavičky a navigace se měří ResizeObserverem (systémové zvětšení písma
 * na Androidu roste v rem, px konstanta by obsah ořízla) a publikují se jako
 * --lks-header-real / --lks-nav-real na kořeni shellu; padding mainu z nich
 * vychází. --lks-toast-top na <html> říká toastu, kde končí hlavička
 * (ToastProvider je v kořeni aplikace, context shellu nevidí).
 */

export type AppShellVariant = "user" | "admin" | "judge" | "bare";

/**
 * Cíl tlačítka Zpět: pevná cesta, krok zpět v historii (bez historie
 * /user), krok zpět s vlastní náhradní cestou pro stránku otevřenou přímo
 * (odkaz, záložka – nastavení živého závodu: hlavní rozhodčí zpět do Živých
 * výsledků), nebo bez tlačítka.
 */
export type BackTarget = { to: string } | "history" | { history: true; fallback: string } | false;

/** Klíč cíle pro závislosti efektů (objekt cíle se mění s každým vykreslením). */
export function backTargetKey(back: BackTarget | undefined): string {
  if (back === undefined) return "";
  if (back === false) return "false";
  if (back === "history") return "history";
  return "to" in back ? `to:${back.to}` : `history:${back.fallback}`;
}

export interface HeaderConfig {
  title?: string;
  subtitle?: string;
  back?: BackTarget;
  actions?: React.ReactNode;
}

interface AppHeaderContextValue {
  setHeader: (config: HeaderConfig) => void;
}

const AppHeaderContext = createContext<AppHeaderContextValue | null>(null);

/**
 * Nastavení hlavičky z obrazovky (titulek, podtitulek, cíl zpět, akce vpravo).
 * Mimo AppShell vrací no-op, aby šel hook bezpečně volat i na bare obrazovkách.
 */
export function useAppHeader(): AppHeaderContextValue {
  const ctx = useContext(AppHeaderContext);
  return ctx ?? { setHeader: noopSetHeader };
}

const noopSetHeader = () => {};

/** Barva hlavičky podle varianty → <meta name="theme-color"> (status bar PWA). */
const THEME_COLORS: Record<AppShellVariant, string> = {
  user: "#004d40",
  admin: "#b4600f",
  judge: "#1a3a2a",
  bare: "#004d40",
};

const DEFAULT_TITLES: Record<AppShellVariant, string> = {
  user: "LKS Apka",
  admin: "Administrace",
  judge: "Rozhodčí",
  bare: "LKS Apka",
};

/** Pozice scrollu per pathname – modulová mapa přežije i výměnu variant shellu. */
const scrollPositions = new Map<string, number>();

// ── Chybová hranice routy ─────────────────────────────────────────
// Pád renderu jedné obrazovky nesmí zbílit celou aplikaci; místo toho
// hláška s tlačítkem „Zkusit znovu" (remount podstromu). Používá ji i
// obrazovka rozhodčího mimo AppShell (App.tsx, fáze 3 – A4).
// Líně načítaná obrazovka (ui/lazyRoute), která se nestáhla (offline), tu
// dostane vlastní hlášku; po návratu připojení se zkusí načíst sama.

interface BoundaryProps {
  resetKey: string;
  children: React.ReactNode;
  /**
   * Po pádu se obrazovka sama zkusí vykreslit znovu za tolik ms (kiosk
   * tabule /display – fáze 4a, E3; na TV nikdo „Zkusit znovu“ nezmáčkne).
   */
  autoRetryMs?: number;
  /**
   * Obal chybové hlášky – pro obrazovky, které si AppShell kreslí samy
   * (Živé výsledky): hláška pak má stejnou hlavičku jako obrazovka.
   */
  frame?: (content: React.ReactNode) => React.ReactNode;
}

interface BoundaryState {
  error: Error | null;
}

export class RouteErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pády po sobě (autoRetryMs) – prodleva se zdvojnásobuje, nejvýš 10 min. */
  private crashes = 0;
  private listeningOnline = false;

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  /** Nový pokus: obrazovky, které se nestáhly, se stáhnou znovu. */
  private retry = () => {
    resetFailedLazyRoutes();
    this.setState({ error: null });
  };

  /** Chunk obrazovky se nestáhl (offline) – po připojení nový pokus sám. */
  private onOnline = () => {
    if (this.state.error && isChunkLoadError(this.state.error)) this.retry();
  };

  private syncOnlineListener() {
    const want = !!this.state.error && isChunkLoadError(this.state.error);
    if (want === this.listeningOnline || typeof window === "undefined") return;
    if (want) window.addEventListener("online", this.onOnline);
    else window.removeEventListener("online", this.onOnline);
    this.listeningOnline = want;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportError("RouteErrorBoundary", error, {
      componentStack: info.componentStack,
      path: window.location.pathname,
    });
    this.syncOnlineListener();
    // Kiosk: nový pokus po pádu sám za autoRetryMs. Nestažená obrazovka
    // (výpadek sítě) prodlevu nezvyšuje – jinak by pozdější skutečný pád
    // čekal minuty; o návrat připojení se stará i posluchač online.
    if (this.props.autoRetryMs) {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      const chunk = isChunkLoadError(error);
      const delay = chunk
        ? this.props.autoRetryMs
        : Math.min(this.props.autoRetryMs * Math.pow(2, this.crashes), 10 * 60 * 1000);
      if (!chunk) this.crashes += 1;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.retry();
      }, delay);
    }
  }

  componentDidUpdate(prev: BoundaryProps, prevState: BoundaryState) {
    // Navigace na jinou routu chybu maže – nová obrazovka dostane čistý start.
    // Smaže se jen chyba předchozí obrazovky: chybu, která přišla s touto
    // navigací (přechod react-routeru ji doručí ve stejném commitu), hranice
    // ukáže – jinak by se nestažená obrazovka hned stahovala podruhé.
    if (prev.resetKey !== this.props.resetKey) {
      this.crashes = 0;
      resetFailedLazyRoutes();
      if (prevState.error && this.state.error === prevState.error) this.setState({ error: null });
    }
    this.syncOnlineListener();
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.listeningOnline) window.removeEventListener("online", this.onOnline);
    this.listeningOnline = false;
    // Odchod z obrazovky, která se nestáhla: příští otevření stahuje znovu
    if (this.state.error && isChunkLoadError(this.state.error)) resetFailedLazyRoutes();
  }

  render() {
    if (this.state.error) {
      const chunk = isChunkLoadError(this.state.error);
      const content = (
        <div className="lks-route-error" role="alert">
          <i className={chunk ? "bi bi-wifi-off" : "bi bi-exclamation-triangle"} aria-hidden="true" />
          {chunk ? (
            <>
              <h2>Obrazovku se nepodařilo načíst</h2>
              <p>
                Zkontrolujte připojení k internetu a zkuste to znovu. Po
                připojení se obrazovka načte sama.
              </p>
              <NewVersionReload />
            </>
          ) : (
            <>
              <h2>Něco se pokazilo</h2>
              <p>
                Obrazovku se nepodařilo zobrazit. Zkuste to znovu; pokud problém
                přetrvá, napište nám na{" "}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </p>
            </>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.retry}
          >
            <i className="bi bi-arrow-clockwise me-2" aria-hidden="true" />
            Zkusit znovu
          </button>
        </div>
      );
      return <>{this.props.frame ? this.props.frame(content) : content}</>;
    }
    return <>{this.props.children}</>;
  }
}

/**
 * Chunk obrazovky se nestáhl, i když je připojení: po nasazení nové verze
 * starý chunk na serveru už není a nový pokus nepomůže – nabídne se
 * načtení nové verze (jen tlačítkem, nikdy samo; zjišťuje se jako
 * u rozhodčího přes index.html bez mezipaměti).
 */
const NewVersionReload: React.FC = () => {
  const isNew = useNewAppVersion(true);
  if (!isNew) return null;
  return (
    <p>
      Mezitím vyšla nová verze aplikace – načtěte ji.{" "}
      <button
        type="button"
        className="btn btn-outline-primary btn-sm"
        onClick={() => window.location.reload()}
      >
        Načíst novou verzi
      </button>
    </p>
  );
};

/** Čekání na stažení líně načítané obrazovky (spinner až po 150 ms). */
export const RouteLoading: React.FC = () => <LoadingState text="Načítám obrazovku…" />;

// ── Spodní navigace ───────────────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Prefixy cest, pro které je položka aktivní (mapa, ne startsWith všeho). */
  match: string[];
}

const NAV_ITEMS: NavItem[] = [
  {
    to: "/user",
    label: "Přihlášky",
    icon: "bi-calendar-check",
    match: ["/user", "/payment", "/paymentmembership", "/pdfviewer", "/mapviewer"],
  },
  {
    to: "/results",
    label: "Výsledky",
    icon: "bi-trophy",
    match: ["/results"],
  },
  {
    to: "/profile",
    label: "Profil",
    icon: "bi-person",
    match: ["/profile", "/updateprofile", "/about", "/gdpr"],
  },
];

const ADMIN_NAV_ITEM: NavItem = {
  to: "/admin",
  label: "Admin",
  icon: "bi-gear",
  match: ["/admin"],
};

/**
 * Čtvrtá záložka „Rozhodčí“ (fáze 3b, I1; rozhodnutí 6′): jen pro toho, kdo
 * běžící závod skutečně rozhoduje (hlavní rozhodčí, liveJudgeUids) – ne pro
 * každého správce. Jeden závod → rovnou jeho obrazovka rozhodčího, víc →
 * malý výběr závodu.
 */
const JUDGE_NAV_LABEL = "Rozhodčí";

const BottomNav = React.forwardRef<HTMLElement>((_props, ref) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { role, user } = useAuth();
  const judgeRaces = useJudgeRaces(user ? user.uid : null);
  // Kdo má běžící závod k rozhodování, dostane živé výsledky a nastavení
  // závodu stažené předem (obrazovka rozhodčího je v hlavním bundlu) – na
  // střelnici bez signálu se pak otevřou i offline.
  usePreloadWhenIdle(LIVE_SCREENS, judgeRaces.length > 0);
  const [chooserOpen, setChooserOpen] = useState(false);
  // Výběr závodu zmizel (zbyl jeden závod nebo žádný) – po dalším načtení se
  // nesmí otevřít sám
  const severalRaces = judgeRaces.length > 1;
  useEffect(() => {
    if (!severalRaces) setChooserOpen(false);
  }, [severalRaces]);

  const judgeItem: NavItem | null =
    judgeRaces.length > 0
      ? {
          to: judgeRaces.length === 1 ? `/judge/${judgeRaces[0].id}` : "",
          label: JUDGE_NAV_LABEL,
          icon: "bi-clipboard-check",
          match: ["/judge"],
        }
      : null;

  const items: NavItem[] = [
    ...NAV_ITEMS,
    ...(judgeItem ? [judgeItem] : []),
    ...(role === "admin" || role === "superadmin" ? [ADMIN_NAV_ITEM] : []),
  ];

  const isActive = (item: NavItem) =>
    item.match.some(
      (prefix) => pathname === prefix || pathname.indexOf(prefix + "/") === 0
    );

  const open = (item: NavItem) => {
    if (item === judgeItem && !item.to) {
      setChooserOpen(true);
      return;
    }
    navigate(item.to);
  };

  return (
    <nav ref={ref} className="lks-bottomnav" aria-label="Hlavní navigace">
      <div className="lks-bottomnav__inner">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <button
              key={item.label}
              type="button"
              className={
                active ? "lks-bottomnav__item is-active" : "lks-bottomnav__item"
              }
              aria-current={active ? "page" : undefined}
              aria-haspopup={item === judgeItem && !item.to ? "dialog" : undefined}
              onClick={() => open(item)}
            >
              <i className={`bi ${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
      <Modal
        open={chooserOpen && judgeRaces.length > 1}
        onClose={() => setChooserOpen(false)}
        title="Který závod rozhodujete?"
        size="sm"
      >
        <div className="lks-judge-chooser">
          {judgeRaces.map((race) => (
            <button
              key={race.id}
              type="button"
              className="btn btn-outline-primary w-100 text-start lks-judge-chooser__item"
              onClick={() => {
                setChooserOpen(false);
                navigate(`/judge/${race.id}`);
              }}
            >
              <span className="d-block fw-semibold">{race.name}</span>
              {race.date ? (
                <span className="d-block small text-muted">{formatCzDate(race.date) || race.date}</span>
              ) : null}
            </button>
          ))}
        </div>
      </Modal>
    </nav>
  );
});

BottomNav.displayName = "BottomNav";

// ── AppShell ──────────────────────────────────────────────────────

export interface AppShellProps {
  variant: AppShellVariant;
  /** Výchozí titulek; obrazovka ho může přepsat přes useAppHeader/Screen. */
  title?: string;
  subtitle?: string;
  back?: BackTarget;
  actions?: React.ReactNode;
  /** Spodní navigace; výchozí jen pro variant="user". */
  showNav?: boolean;
  /**
   * Účet vpravo v hlavičce – jen pro přihlášeného, jen na obrazovce bez
   * tlačítka Zpět a jen ve variantách user/admin. Výchozí: administrace
   * a uživatelská část se spodní navigací; přihlášení a stavové stránky
   * (user bez navigace) ho nemají.
   */
  account?: boolean;
  /** main bez vnitřního paddingu (PdfViewer, MapViewer). */
  fullscreen?: boolean;
  /** Přebití maximální šířky obsahu v px (jinak token podle varianty). */
  maxWidth?: number;
  children?: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
  variant,
  title,
  subtitle,
  back = false,
  actions,
  showNav = variant === "user",
  account = variant === "admin" || (variant === "user" && showNav),
  fullscreen = false,
  maxWidth,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  const defaults: HeaderConfig = {
    title: title ?? DEFAULT_TITLES[variant],
    subtitle,
    back,
    actions,
  };

  // Přepis hlavičky z obrazovky je svázaný s cestou, pro kterou vznikl:
  // navigace na jinou routu ho tím automaticky zneplatní (žádný reset efekt,
  // který by kvůli pořadí efektů dítě–rodič přepsal čerstvě nastavené hodnoty).
  const [override, setOverride] = useState<{
    path: string;
    config: HeaderConfig;
  } | null>(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const setHeader = useCallback((config: HeaderConfig) => {
    const defined: HeaderConfig = {};
    if (config.title !== undefined) defined.title = config.title;
    if (config.subtitle !== undefined) defined.subtitle = config.subtitle;
    if (config.back !== undefined) defined.back = config.back;
    if (config.actions !== undefined) defined.actions = config.actions;
    setOverride({ path: pathRef.current, config: defined });
  }, []);
  const contextValue = useRef<AppHeaderContextValue>({ setHeader });

  const effective: HeaderConfig =
    override && override.path === location.pathname
      ? { ...defaults, ...override.config }
      : defaults;

  // ── Měřené výšky hlavičky a navigace → CSS proměnné na shellu ──
  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const update = () => {
      const h = headerRef.current ? headerRef.current.offsetHeight : 0;
      const n = navRef.current ? navRef.current.offsetHeight : 0;
      shell.style.setProperty("--lks-header-real", `${h}px`);
      // Bez spodní navigace musí spodní padding mainu zůstat na safe-area
      // (0px by fallback var() v CSS zahodilo – obsah pod home indicatorem).
      shell.style.setProperty(
        "--lks-nav-real",
        n > 0 ? `${n}px` : "var(--safe-bottom, 0px)"
      );
      try {
        // Toasty jsou nahoře pod hlavičkou (dole by překryly sticky lištu
        // formuláře a spodní navigaci) – ToastProvider je v kořeni mimo
        // shell, proto výška hlavičky jde přes proměnnou na <html>.
        document.documentElement.style.setProperty(
          "--lks-toast-top",
          h > 0 ? `${h}px` : "var(--safe-top, 0px)"
        );
      } catch {
        /* diagnostika nesmí shodit shell */
      }
    };
    update();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      if (headerRef.current) observer.observe(headerRef.current);
      if (navRef.current) observer.observe(navRef.current);
    }
    return () => {
      if (observer) observer.disconnect();
      document.documentElement.style.removeProperty("--lks-toast-top");
    };
  }, [variant, showNav]);

  // ── Otevřená klávesnice (dotyková zařízení): skrýt sticky prvky ──
  // Námitka kritika č. 10: sticky .lks-form-actions nesmí překrýt fokusované
  // pole nad klávesnicí. Fokus v input/textarea/select + malý visualViewport
  // přepnou na shell třídu lks-shell--kbd (lišta přejde do toku stránky).
  useEffect(() => {
    const coarse =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    if (!coarse) return;
    const shell = shellRef.current;
    if (!shell) return;

    const update = () => {
      const ae = document.activeElement;
      const typing =
        !!ae &&
        /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) &&
        shell.contains(ae);
      const vv = window.visualViewport;
      const small = vv ? vv.height < 480 : false;
      if (typing || small) shell.classList.add("lks-shell--kbd");
      else shell.classList.remove("lks-shell--kbd");
    };
    const updateDeferred = () => {
      // focusout přijde dřív, než se fokus přesune na další prvek
      setTimeout(update, 0);
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", updateDeferred);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", update);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", updateDeferred);
      if (vv) vv.removeEventListener("resize", update);
    };
  }, []);

  // ── theme-color podle varianty (status bar nainstalované PWA) ──
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const previous = meta.getAttribute("content");
    meta.setAttribute("content", THEME_COLORS[variant]);
    return () => {
      if (previous) meta.setAttribute("content", previous);
    };
  }, [variant]);

  // ── Scroll: reset při nové navigaci, obnova pozice při Zpět (POP) ──
  // Pozice se ukládá průběžně pasivním listenerem. Číst scrollTop až v cleanupu
  // nejde: React vymění DOM obsah routy dřív, než cleanup běží, a prohlížeč
  // hodnotu ořízne podle kratší nové obrazovky.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const path = location.pathname;
    const save = () => scrollPositions.set(path, main.scrollTop);
    main.addEventListener("scroll", save, { passive: true });
    return () => main.removeEventListener("scroll", save);
  }, [location.pathname]);

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const saved =
      navigationType === "POP" ? scrollPositions.get(location.pathname) : undefined;
    if (!saved) {
      main.scrollTop = 0;
      return;
    }
    // Obrazovka typicky teprve načítá data a je krátká – obnovu pozice
    // zkoušíme po snímcích, dokud obsah nedoroste (okno ~0,5 s).
    let frames = 0;
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      main.scrollTop = saved;
      if (main.scrollTop + 1 < saved && frames++ < 30) {
        requestAnimationFrame(apply);
      }
    };
    apply();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigationType]);

  // ── Zpět ──
  const handleBack = () => {
    const target = effective.back;
    if (!target) return;
    if (target === "history") {
      if (window.history.length > 1) navigate(-1);
      else navigate("/user");
      return;
    }
    if ("history" in target) {
      if (window.history.length > 1) navigate(-1);
      else navigate(target.fallback);
      return;
    }
    navigate(target.to);
  };

  // Výchozí akce admin hlavičky: opustit administraci. Ikona domečku (jako
  // „Na hlavní stránku" v NotFound), ne panáček – ten patří účtu hned vedle
  // a bez vyplněného jména by se s ním pletl. Popisek se schová až na
  // nejužších displejích, kde by vedle účtu zkrátil titulek (ui.css).
  const defaultAdminActions =
    variant === "admin" ? (
      <button
        type="button"
        className="lks-header__btn lks-header__btn--home"
        aria-label="Přejít do uživatelské části"
        onClick={() => navigate("/user")}
      >
        <i className="bi bi-house" aria-hidden="true" />
        <span className="lks-header__home-text">Uživatel</span>
      </button>
    ) : null;

  const headerActions =
    effective.actions !== undefined && effective.actions !== null
      ? effective.actions
      : defaultAdminActions;

  // Účet jen na hlavních obrazovkách (bez Zpět) – podstránky mají vpravo
  // akce a vlevo Zpět, titulek by se nevešel. Přihlášení hlídá AccountMenu.
  const showAccount =
    account && (variant === "user" || variant === "admin") && !effective.back;

  const contentStyle = maxWidth ? { maxWidth: `${maxWidth}px` } : undefined;

  return (
    <AppHeaderContext.Provider value={contextValue.current}>
      <div
        ref={shellRef}
        className={`lks-shell lks-shell--${variant}${
          showNav ? " lks-shell--with-nav" : ""
        }`}
      >
        {variant !== "bare" && (
          <header ref={headerRef} className={`lks-header lks-header--${variant}`}>
            <div className="lks-header__side lks-header__side--left">
              {effective.back ? (
                <button
                  type="button"
                  className="lks-header__btn"
                  aria-label="Zpět"
                  onClick={handleBack}
                >
                  <i className="bi bi-arrow-left" aria-hidden="true" />
                  <span className="lks-header__btn-text">Zpět</span>
                </button>
              ) : null}
            </div>
            <div className="lks-header__titles">
              <h1 className="lks-header__title">{effective.title}</h1>
              {effective.subtitle ? (
                <div className="lks-header__subtitle">{effective.subtitle}</div>
              ) : null}
            </div>
            <div className="lks-header__side lks-header__side--right">
              {headerActions}
              {showAccount ? <AccountMenu /> : null}
            </div>
          </header>
        )}

        <main
          ref={mainRef}
          className={`lks-main${fullscreen ? " lks-main--fullscreen" : ""}`}
        >
          <OfflineBanner />
          <RouteErrorBoundary resetKey={location.pathname}>
            {/* Líně načítané obrazovky (ui/lazyRoute): hlavička a navigace
                zůstanou, spinner jen v obsahu */}
            <Suspense fallback={<RouteLoading />}>
              {fullscreen ? (
                children ?? <Outlet />
              ) : (
                <div
                  className={`lks-page lks-page--${variant}`}
                  style={contentStyle}
                >
                  {children ?? <Outlet />}
                </div>
              )}
            </Suspense>
          </RouteErrorBoundary>
        </main>

        {showNav && <BottomNav ref={navRef} />}
      </div>
    </AppHeaderContext.Provider>
  );
};

// ── Screen – deklarativní nastavení hlavičky pro jednu routu ─────

export interface ScreenProps extends HeaderConfig {
  children: React.ReactNode;
}

/**
 * Obal routy v App.tsx: nastaví hlavičce shellu titulek, podtitulek, cíl
 * tlačítka Zpět a akce. Obrazovky ho později nahradí vlastním voláním
 * useAppHeader (budou titulek měnit podle načtených dat).
 */
export const Screen: React.FC<ScreenProps> = ({
  title,
  subtitle,
  back,
  actions,
  children,
}) => {
  const { setHeader } = useAppHeader();
  const backKey = backTargetKey(back);

  useLayoutEffect(() => {
    setHeader({ title, subtitle, back, actions });
    // actions jsou v kroku 2 vždy statické JSX z route tabulky; back je objekt
    // nový při každém vykreslení – závislostí je jeho klíč backKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeader, title, subtitle, backKey]);

  // Vlastní Suspense: titulek hlavičky se nastaví hned, i když se líně
  // načítaná obrazovka (ui/lazyRoute) ještě stahuje. Klíč = titulek (každá
  // routa má svůj): sourozenecké routy pod jedním layoutem jinak sdílejí
  // instanci Screen a navigace v přechodu (react-router startTransition) by
  // nechala starou obrazovku i titulek bez spinneru až do stažení. Obrazovky
  // sourozeneckých rout jsou různé komponenty – remount nic nemění.
  return (
    <Suspense key={title} fallback={<RouteLoading />}>
      {children}
    </Suspense>
  );
};

// ── Offline banner ────────────────────────────────────────────────

/**
 * Nenásilný indikátor výpadku připojení – pruh v toku stránky pod hlavičkou
 * (vykresluje ho AppShell nahoře v mainu, nic nepřekrývá a nebere doteky).
 * Aplikace zůstává namountovaná: Firestore má persistentní cache a zápisy
 * odešle po připojení. Nahrazuje dřívější odmountování celé aplikace.
 * /judge a /display (mimo shell) mají vlastní offline indikaci.
 */
const OfflineBanner: React.FC = () => {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  if (online) return null;

  return (
    <div className="lks-offline" role="status">
      <i className="bi bi-wifi-off" aria-hidden="true" />
      <span>Jste offline – změny se odešlou po připojení</span>
    </div>
  );
};

/**
 * Odscrollování na prvek uvnitř .lks-main (např. formulář po kliknutí na
 * Upravit). window.scrollTo od kroku 2 nic nedělá – scrolluje main – a holé
 * scrollIntoView(block:'start') zajede pod fixní hlavičku; tady se odečítá
 * padding mainu (≈ výška hlavičky).
 */
export function scrollIntoMain(el: HTMLElement | null): void {
  if (!el) return;
  const main = el.closest(".lks-main") as HTMLElement | null;
  if (!main) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  // getBoundingClientRect, ne offsetTop: ten se vztahuje k nejbližšímu
  // pozicovanému předkovi (Bootstrap .card má position: relative).
  const padTop = parseFloat(getComputedStyle(main).paddingTop) || 0;
  const delta =
    el.getBoundingClientRect().top - main.getBoundingClientRect().top;
  // Toasty visí nahoře pod hlavičkou – cíl nesmí skončit pod nimi.
  const stack = document.querySelector(".lks-toasts") as HTMLElement | null;
  const toastPad =
    stack && stack.childElementCount > 0 ? stack.offsetHeight + 8 : 0;
  const top = Math.max(0, main.scrollTop + delta - padTop - 8 - toastPad);
  try {
    main.scrollTo({ top, behavior: "smooth" });
  } catch {
    main.scrollTop = top;
  }
}

/**
 * Offline indikace pro živou tabuli (/display): nenásilná pilulka přes
 * obrazovku, pointer-events none (nic neblokuje). Tabule na projektoru jinak
 * při pádu Wi-Fi tiše ukazuje zamrzlé výsledky z Firestore cache – před
 * redesignem výpadek odmountoval celou aplikaci, takže byl aspoň vidět.
 * JudgeView má vlastní indikátor, tam se nepoužívá.
 */
export const OfflineOverlay: React.FC = () => {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  if (online) return null;

  return (
    <div className="lks-offline-overlay" role="status">
      <i className="bi bi-wifi-off" aria-hidden="true" />
      <span>Offline – výsledky se nemusí obnovovat</span>
    </div>
  );
};

export default AppShell;
